import {
  DarwinButton,
  DarwinCol,
  DarwinDivider,
  DarwinFlex,
  DarwinSpin,
} from 'components/darwin-ui';
import { useTranslation } from 'react-i18next';
import styles from '../Organizations.module.css';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useState } from 'react';
import { Form } from 'antd';
import { useForm, useWatch } from 'react-hook-form';
import { GenericInput, GenericInputProps } from 'components/ui/GenericInput';
import { ErrorType, InputType } from 'types/genericTypes';
import { useUpdateExternalOrganizationWithoutKey } from 'api/services/organizationService';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useUI } from 'contexts/UiContext';
import { useQueryClient } from '@tanstack/react-query';
import { useDeleteEUDRDocByFieldId } from 'api/services/eudrDocumentsService';
import { EudrSectionShell } from 'components/ui/EudrSectionShell';

export type EudrRegulationDocument =
  | 'propertyDocuments'
  | 'noDeforestation'
  | 'signedStatement'
  | 'eudrFile'
  | 'processDocument';

export const EudrRegulationDocumentType = {
  propertyDocuments: 'propertyDocumentIds',
  noDeforestation: 'noDeforestationProofIds',
  signedStatement: 'signedStatementIds',
  eudrFile: 'eudrFileIds',
  processDocument: 'processDocumentIds',
};

type EudrRegulationFormData = {
  eoriNumber: string;
  propertyDocuments: string[];
  noDeforestation: string[];
  signedStatement: string[];
};

const defaultValuesForm = {
  eoriNumber: '',
  propertyDocuments: [''],
  noDeforestation: [''],
  signedStatement: [''],
};

type EudrRegulationSectionProps = {
  targetCompanyId: string;
  name: string;
  fiscalId: string;
  data: any;
};

export const EudrRegulationSection = ({
  name,
  fiscalId,
  targetCompanyId,
  data,
}: EudrRegulationSectionProps): JSX.Element => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const { selectedCompany, userPrimaryCompany } = useUI();
  const [initialValuesOnEditForm, setInitialValuesOnEditForm] = useState({});
  const [eudrRegulation, setEudrRegulation] = useState<boolean>(false);
  const [initialized, setInitialized] = useState(false);
  const queryClient = useQueryClient();
  //update organization without key
  const {
    mutateAsync: updateExternalOrganizationWithoutKey,
    isPending: isUpdatingExternalOrganizationWithoutKeyPending,
  } = useUpdateExternalOrganizationWithoutKey();
  //delete eudr document by field id
  const {
    mutateAsync: deleteEudrDocument,
    isPending: isDeletingEudrDocumentPending,
  } = useDeleteEUDRDocByFieldId();

  const removeDocumentHandler = useCallback(
    async (eudrId: string) => {
      try {
        await deleteEudrDocument({
          fieldId: eudrId,
          companyId: selectedCompany || '',
          targetCompanyId: targetCompanyId,
        });
        openNotification(
          t('success'),
          t('eudr-regulation.delete-success-message'),
          'success'
        );
        queryClient.invalidateQueries({ queryKey: ['organization'] });
      } catch (err) {
        const apiError = err as ErrorType;
        if (apiError.body?.errorCode) {
          openNotification(t('error'), t(apiError.body.errorCode), 'error');
        } else {
          openNotification(
            t('error'),
            t('eudr-regulation.delete-error-message'),
            'error'
          );
        }
      }
    },
    [
      deleteEudrDocument,
      openNotification,
      queryClient,
      selectedCompany,
      targetCompanyId,
      t,
    ]
  );

  const DocumentHandler = useCallback(
    async (id: string, type: EudrRegulationDocument) => {
      try {
        const payload = {
          name: name,
          fiscalId: fiscalId,
          [type]: [id],
          contractAddress: userPrimaryCompany?.contractAddress || '',
        };
        await updateExternalOrganizationWithoutKey({
          companyData: payload,
          companyId: selectedCompany || '',
          targetCompanyId: targetCompanyId || '',
        });
        openNotification(
          t('success'),
          t('eudr-regulation.file-upload-success'),
          'success'
        );
        queryClient.invalidateQueries({ queryKey: ['organization'] });
      } catch (err) {
        const apiError = err as ErrorType;
        if (apiError.body?.errorCode) {
          openNotification(t('error'), t(apiError.body.errorCode), 'error');
        } else {
          openNotification(
            t('error'),
            t('eudr-regulation.delete-error-message'),
            'error'
          );
        }
      }
    },
    [
      openNotification,
      t,
      name,
      fiscalId,
      userPrimaryCompany,
      updateExternalOrganizationWithoutKey,
      selectedCompany,
      targetCompanyId,
      queryClient,
    ]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EudrRegulationFormData>({
    defaultValues: {
      ...defaultValuesForm,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!data || initialized) return;

    const enabled = !!data?.isEudrEnabled;
    const eori = data?.eoriNumber ?? '';

    setEudrRegulation(enabled);
    setInitialValuesOnEditForm({ eoriNumber: eori });
    reset({ eoriNumber: eori });
    setInitialized(true);
  }, [data, initialized, reset]);

  useEffect(() => {
    setInitialized(false);
  }, [data?.id]);

  //Logic for disabled button
  const currentValues = useWatch({ control });
  const isEditForm =
    JSON.stringify(currentValues) === JSON.stringify(initialValuesOnEditForm);
  const isNewForm =
    JSON.stringify(currentValues) === JSON.stringify(defaultValuesForm);

  const disableButton = isEditForm || isNewForm;

  const isUploadingOrDeleting =
    isDeletingEudrDocumentPending ||
    isUpdatingExternalOrganizationWithoutKeyPending;

  const dataTableInputs: Omit<
    GenericInputProps<EudrRegulationFormData>,
    'control' | 'errors'
  >[] = [
    {
      inputType: InputType.INPUT,
      fieldName: 'eoriNumber',
      label: t('eudr-regulation.eori-number'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('eudr-regulation.eori-number-required'),
      },
      maxLength: 50,
      tooltipIcon: <InfoCircleOutlined />,
      tooltipText: t('eudr-regulation.eori-number-tooltip'),
    },
    {
      inputType: InputType.UPLOAD,
      fieldName: 'propertyDocuments',
      label: t('eudr-regulation.property-documents'),
      inputRules: {},
      tooltipIcon: <InfoCircleOutlined />,
      tooltipText: t('eudr-regulation.property-documents-tooltip'),
      documentHandler: DocumentHandler,
      removeDocumentHandler: removeDocumentHandler,
      type: EudrRegulationDocumentType.propertyDocuments as EudrRegulationDocument,
      listedDocuments: data?.propertyDocuments || [],
    },
    {
      inputType: InputType.UPLOAD,
      fieldName: 'noDeforestation',
      label: t('eudr-regulation.no-deforestation'),
      inputRules: {},
      tooltipIcon: <InfoCircleOutlined />,
      tooltipText: t('eudr-regulation.no-deforestation-tooltip'),
      documentHandler: DocumentHandler,
      removeDocumentHandler: removeDocumentHandler,
      type: EudrRegulationDocumentType.noDeforestation as EudrRegulationDocument,
      listedDocuments: data?.noDeforestationProofs || [],
    },
    {
      inputType: InputType.UPLOAD,
      fieldName: 'signedStatement',
      label: t('eudr-regulation.signed-statement'),
      inputRules: {},
      tooltipIcon: <InfoCircleOutlined />,
      tooltipText: t('eudr-regulation.signed-statement-tooltip'),
      documentHandler: DocumentHandler,
      removeDocumentHandler: removeDocumentHandler,
      type: EudrRegulationDocumentType.signedStatement as EudrRegulationDocument,
      listedDocuments: data?.signedStatements || [],
    },
  ];

  const saveOrganization = useCallback(
    async (opts?: { eoriNumber?: string }) => {
      const eori = eudrRegulation ? opts?.eoriNumber?.trim() ?? '' : '';

      const payload = {
        name,
        fiscalId,
        isEudrEnabled: eudrRegulation,
        eoriNumber: eori,
        contractAddress: userPrimaryCompany?.contractAddress ?? '',
      };

      setInitialValuesOnEditForm({});

      try {
        await updateExternalOrganizationWithoutKey({
          companyData: payload,
          companyId: selectedCompany ?? '',
          targetCompanyId: targetCompanyId ?? '',
        });

        await queryClient.invalidateQueries({ queryKey: ['organization'] });

        openNotification(
          t('success'),
          t('eudr-regulation.edit-success-message'),
          'success'
        );
      } catch (err) {
        const apiError = err as ErrorType;
        if (apiError.body?.errorCode) {
          openNotification(t('error'), t(apiError.body.errorCode), 'error');
        } else {
          openNotification(
            t('error'),
            t('eudr-regulation.edit-error-message'),
            'error'
          );
        }
      }
    },
    [
      name,
      fiscalId,
      eudrRegulation,
      userPrimaryCompany?.contractAddress,
      selectedCompany,
      targetCompanyId,
      updateExternalOrganizationWithoutKey,
      queryClient,
      openNotification,
      t,
      setInitialValuesOnEditForm,
    ]
  );

  const onFormSubmit = async (data: EudrRegulationFormData) => {
    await saveOrganization({ eoriNumber: data.eoriNumber });
  };

  const saveWhenDisable = useCallback(() => {
    saveOrganization({ eoriNumber: '' });
  }, [saveOrganization]);

  const cancelButtonHandler = () => {
    reset(defaultValuesForm);
    setInitialValuesOnEditForm({});
    setEudrRegulation(false);
  };

  const checkboxChangeHandler = (checked: boolean) => {
    setInitialized(true);
    if (!checked) {
      setEudrRegulation(false);
      reset(defaultValuesForm);
      setInitialValuesOnEditForm({});
    } else {
      setEudrRegulation(true);
    }
  };

  const extraSaveButton = !!data?.isEudrEnabled;

  return (
    <>
      <DarwinSpin spinning={isUploadingOrDeleting} />
      <div className={styles.eudrSectionWrapper}>
      <EudrSectionShell
        title={t('eudr-regulation.title')}
        description={t('eudr-regulation.tooltip-text')}
        checked={eudrRegulation}
        onCheckChange={checkboxChangeHandler}
        checkboxLabel={t('eudr-regulation.checkbox-title')}
        checkboxTooltip={t('eudr-regulation.tooltip-text')}
        checkboxExtra={
          extraSaveButton && !eudrRegulation ? (
            <DarwinFlex className={styles.extraButtonSave}>
              <DarwinButton type="primary" onClick={saveWhenDisable}>
                {t('eudr-regulation.save-button')}
              </DarwinButton>
            </DarwinFlex>
          ) : undefined
        }
      >
        <DarwinDivider />
        <Form
          layout="vertical"
          style={{ width: '100%' }}
          onFinish={handleSubmit(onFormSubmit)}
        >
          <DarwinCol>
            {dataTableInputs.map(inputProps => (
              <GenericInput<EudrRegulationFormData>
                key={inputProps.fieldName}
                control={control}
                errors={errors}
                {...inputProps}
              />
            ))}
          </DarwinCol>
        </Form>
        <DarwinFlex
          style={{
            justifyContent: 'end',
            gap: 8,
            width: '100%',
          }}
        >
          <DarwinButton type="default" onClick={() => cancelButtonHandler()}>
            {t('eudr-regulation.cancel-button')}
          </DarwinButton>
          <DarwinButton
            type="primary"
            onClick={handleSubmit(onFormSubmit)}
            disabled={disableButton}
          >
            {t('eudr-regulation.save-button')}
          </DarwinButton>
        </DarwinFlex>
      </EudrSectionShell>
      </div>
    </>
  );
};
